import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Dialog, DialogContent } from '@/components/ui/dialog';

import { AdjustedQuotesBanner } from '@/components/AdjustedQuotesBanner';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ShopInsuranceAcknowledgmentOverlay } from '@/components/ShopInsuranceAcknowledgmentOverlay';
import {
  ShopInsuranceNoticeBannerDesktop,
  ShopInsuranceNoticeBannerMobile,
} from '@/components/ShopInsuranceNoticeBanner';
import { UnderwritingSlideOut } from '@/components/UnderwritingSlideOut';
import { ShopInsuranceFiltersSidebar } from '@client/components/shop-insurance/ShopInsuranceFiltersSidebar';
import { ShopInsuranceHeader } from '@client/components/shop-insurance/ShopInsuranceHeader';
import { ShopInsuranceTabContent } from '@client/components/shop-insurance/ShopInsuranceTabContent';
import { ShopInsuranceTabsWrapper } from '@client/components/shop-insurance/ShopInsuranceTabsWrapper';
import { getAiPromptType } from '@client/util';

import { FormProvider, useForm } from 'react-hook-form';

import {
  DEFAULT_FILTERS_STATE,
  useAdjustedQuotes,
  type CombinedFilterType,
} from '@client/hooks/useAdjustedQuotes';
import { useDebounce } from '@client/hooks/useDebounce';
import { useErrorToasts } from '@client/hooks/useErrorToasts';
import { useFilteredTraditionalLTCQuotes } from '@client/hooks/useFilteredTraditionalLTCQuotes';
import { useProductDiscovery } from '@client/hooks/useProductDiscovery';
import { useQuoteFetching } from '@client/hooks/useQuoteFetching';
import { useQuoteSelection } from '@client/hooks/useQuoteSelection';
import { useShopInsuranceAcknowledgment } from '@client/hooks/useShopInsuranceAcknowledgment';
import {
  mapMaritalStatusToTraditionalLTC,
  useShopInsuranceState,
} from '@client/hooks/useShopInsuranceState';
import { SliderBounds, useSliderBounds } from '@client/hooks/useSliderBounds';
import { useTraditionalLTCQuoteFetching } from '@client/hooks/useTraditionalLTCQuoteFetching';
import { useUnderwritingResults } from '@client/hooks/useUnderwritingResults';
import { useUnderwritingSearch } from '@client/hooks/useUnderwritingSearch';
import { useSelector } from 'react-redux';

import {
  selectClient,
  selectClientCalculationSettings,
  selectMaybeClientPartner,
  selectQuotes,
  selectQuotesError,
  selectQuotesIsLoading,
} from '@client/model';
import { selectFeatureFlags, selectSessionAdvisor } from '@client/model/selectors';
import { calculateAge, zipCodeToState } from '@shared';
import { carescoutAvailableStates } from '@shared/carrierStateAvailability';

interface FormData {
  premium: number;
  benefitDuration: CombinedFilterType;
  inflationProtection: CombinedFilterType;
}

interface ShopInsuranceContextType {
  sliderBounds: SliderBounds;
  paymentOption: PaymentOption;
  showTraditionalLTCOptions?: boolean;
  showSinglePayAnd10Pay?: boolean;
  handlePaymentOptionChange: (value: PaymentOption) => void;
}

interface ShopInsuranceModalPropsOptions {
  paymentOption?: 'Single Pay' | '10-Pay' | 'Annually' | 'Monthly';
  buyerOption?: '1-buyer' | '2-buyer';
  selectTabOption?: ShopInsuranceTab;
  skipAutoFetch?: boolean;
}

export type PaymentOption = 'Single Pay' | '10-Pay' | 'Annually' | 'Monthly';

const MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY = 40;

const ShopInsuranceContext = createContext<ShopInsuranceContextType | undefined>(undefined);

export function useShopInsuranceContext() {
  const context = useContext(ShopInsuranceContext);

  if (!context) {
    throw new Error('useShopInsuranceContext must be used within a ShopInsuranceProvider');
  }

  return context;
}

export function ShopInsuranceModal({
  open,
  onClose,
  queryOptions,
}: {
  open: boolean;
  onClose: () => void;
  queryOptions?: ShopInsuranceModalPropsOptions;
}) {
  const skipAutoFetch = queryOptions?.skipAutoFetch ?? false;
  useErrorToasts({ open });

  const quotes = useSelector(selectQuotes);
  const client = useSelector(selectClient);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);
  const clientAge = calculateAge(new Date(client.intakeSurvey.clientBirthDate));
  const [selectedEligibilityFilters, setSelectedEligibilityFilters] = useState<string[]>([
    'eligible',
    'unsure',
    'ineligible',
  ]);
  const [selectedFeatureBenefitsFilters, setSelectedFeatureBenefitsFilters] = useState<string[]>(
    [],
  );

  // Underwriting slideout state
  const [showUnderwritingSlideOut, setShowUnderwritingSlideOut] = useState(false);
  const [selectedUnderwritingData, setSelectedUnderwritingData] = useState<{
    data: OpenAiCompletionDbRecord | null;
    productName: string;
  } | null>(null);

  const error = useSelector(selectQuotesError);
  const isLoading = useSelector(selectQuotesIsLoading);
  const featureFlags = useSelector(selectFeatureFlags);

  const {
    selectedTab,
    paymentOption,
    expandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    setShowAcknowledgment,
    handleBuyerOptionChange,
    handlePaymentOptionChange,
  } = useShopInsuranceState({
    hideSinglePayAnd10Pay:
      featureFlags.hideSinglePayAnd10Pay || clientAge < MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY,
    providedBuyerOption: queryOptions?.buyerOption,
    providedPaymentOption: queryOptions?.paymentOption,
    providedSelectedTab: queryOptions?.selectTabOption,
  });

  const { fetchQuotesForPolicyType } = useQuoteFetching({
    open,
    selectedTab,
    paymentOption,
    hasPartnerWithDemographics,
    skipAutoFetch,
  });

  const sliderBounds = useSliderBounds(quotes, selectedTab);
  const { isQuoteSelected } = useQuoteSelection({ selectedTab });

  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const formMethods = useForm<FormData>({
    defaultValues: {
      ...DEFAULT_FILTERS_STATE,
      premium: sliderBounds.defaultValue,
    },
  });

  const { watch, reset } = formMethods;

  const premiumValue = useDebounce(watch('premium'), 200);
  const benefitDurationValue = useDebounce(watch('benefitDuration'), 200);
  const inflationProtectionValue = useDebounce(watch('inflationProtection'), 200);

  useEffect(() => {
    if (sliderBounds.isCalculatedFromQuotes) {
      if (premiumValue < sliderBounds.min || premiumValue > sliderBounds.max) {
        reset({
          ...DEFAULT_FILTERS_STATE,
          premium: sliderBounds.defaultValue,
        });
      }
    }
  }, [sliderBounds, premiumValue, reset]);

  useEffect(() => {
    if (sliderBounds.isCalculatedFromQuotes) {
      const fallbackPremium = paymentOption === '10-Pay' ? 10000 : 100000;
      const defaultPremium = Math.max(sliderBounds.min, fallbackPremium);
      reset({
        ...DEFAULT_FILTERS_STATE,
        premium: defaultPremium,
      });
    }
  }, [paymentOption, reset, sliderBounds.isCalculatedFromQuotes, sliderBounds.min]);

  // Use new hooks for Traditional LTC
  useTraditionalLTCQuoteFetching({
    open,
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    advisorId,
  });

  const { filteredTraditionalLTCQuotes } = useFilteredTraditionalLTCQuotes({
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    returnOnInvestmentCalculationType,
  });

  // Use acknowledgment hook
  useShopInsuranceAcknowledgment({
    open,
    isLoading,
    quotes,
    hasAcknowledged,
    setShowAcknowledgment,
  });

  function handleTabChangeWithFetch(tabValue: string) {
    handleTabChange(tabValue);
    fetchQuotesForPolicyType(tabValue as ShopInsuranceTab, paymentOption);
  }

  const { adjustedQuotes, isAdjusted } = useAdjustedQuotes({
    client,
    selectedTab,
    returnOnInvestmentCalculationType,

    originalQuotes: quotes,
    selectedPremium: premiumValue,
    selectedBenefitDuration: benefitDurationValue,
    selectedInflationProtection: inflationProtectionValue,
    selectedFeatureBenefitsFilters,
  });

  // Product discovery
  const { productsToSearch } = useProductDiscovery({
    adjustedQuotes,
    traditionalLTCQuotes: filteredTraditionalLTCQuotes,
    isJoint: selectedTab === 'joint',
  });

  // Underwriting search orchestration
  const {
    fetchStarted: underwritingFetchStarted,
    fetchComplete: underwritingFetchComplete,
    results: searchResults,
  } = useUnderwritingSearch({
    clientId: client?.clientId,
    productsToSearch,
    isJoint: selectedTab === 'joint',
    shouldStart:
      open &&
      !!client?.clientId &&
      (adjustedQuotes.length > 0 || filteredTraditionalLTCQuotes.length > 0),
  });

  // Use new underwriting results hook
  const {
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  } = useUnderwritingResults({
    adjustedQuotes,
    filteredTraditionalLTCQuotes,
    searchResults,
    selectedTab,
    underwritingFetchComplete,
    selectedEligibilityFilters,
  });

  function handleOnClose() {
    localStorage.removeItem('benefitDurationNotice');
    localStorage.removeItem('inflationProtectionNotice');

    onClose();
  }

  const handleViewUnderwritingDetails = (_quoteId: string, productName: string) => {
    let underwritingData: OpenAiCompletionDbRecord | null = null;

    const aiPromptType = getAiPromptType('', productName, selectedTab === 'joint');
    if (aiPromptType) {
      const resultKey = `${productName}:${aiPromptType}`;
      const result = searchResults.get(resultKey);
      if (result?.fullData) {
        underwritingData = result.fullData;
      }
    }

    setSelectedUnderwritingData({
      data: underwritingData,
      productName,
    });
    setShowUnderwritingSlideOut(true);
  };

  const tabContent = useMemo(
    () => (
      <ShopInsuranceTabContent
        underwritingFetchStarted={underwritingFetchStarted}
        underwritingFetchComplete={underwritingFetchComplete}
        filteredAdjustedQuotes={filteredAdjustedQuotes}
        filteredTraditionalLTCQuotesWithEligibility={filteredTraditionalLTCQuotesWithEligibility}
        underwritingResults={underwritingResults}
        expandedIndices={expandedIndices}
        selectedTab={selectedTab}
        clientPartner={clientPartner}
        premiumValue={premiumValue}
        paymentOption={paymentOption}
        advisorId={advisorId}
        isQuoteSelected={isQuoteSelected}
        handleCardToggle={handleCardToggle}
        handleViewUnderwritingDetails={handleViewUnderwritingDetails}
        onClose={onClose}
      />
    ),
    [
      underwritingFetchStarted,
      underwritingFetchComplete,
      filteredAdjustedQuotes,
      filteredTraditionalLTCQuotesWithEligibility,
      underwritingResults,
      expandedIndices,
      selectedTab,
      clientPartner,
      premiumValue,
      paymentOption,
      advisorId,
      isQuoteSelected,
      handleCardToggle,
      onClose,
    ],
  );

  const isAnyLoading = isLoading;
  const anyError = error;
  const noLoadingNoError = !isAnyLoading && !anyError;

  const shopInsuranceFiltersProps = {
    selectedEligibilityFilters,
    setSelectedEligibilityFilters,
    showEligibilityFilter,
    eligibilityCounts,
    selectedFeatureBenefitsFilters,
    setSelectedFeatureBenefitsFilters,
  };

  const maxEligibleAgeForCareScout = 65;
  const isMarriedOrPartnered =
    mapMaritalStatusToTraditionalLTC(client?.intakeSurvey?.clientMaritalStatus) === 'Married' ||
    hasPartnerWithDemographics;

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={() => handleOnClose()}
    >
      <DialogContent
        className="size-full max-h-[96vh] w-[95vw] max-w-[1440px] overflow-y-auto md:min-w-[90vw] lg:min-w-[1200px] lg:overflow-hidden xl:min-w-[1344px] [&>button[data-slot=close-dialog]]:right-2 [&>button[data-slot=close-dialog]]:top-2"
        data-testid="shop-insurance-modal-content"
      >
        <ShopInsuranceContext.Provider
          value={{
            sliderBounds,
            paymentOption,
            handlePaymentOptionChange,
            showTraditionalLTCOptions:
              carescoutAvailableStates.includes(state.stateAbbreviation as any) &&
              clientAge <= maxEligibleAgeForCareScout,
            showSinglePayAnd10Pay:
              !featureFlags.hideSinglePayAnd10Pay && clientAge >= MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY,
          }}
        >
          <FormProvider {...formMethods}>
            <ShopInsuranceAcknowledgmentOverlay
              open={showAcknowledgment && !hasAcknowledged}
              onAcknowledge={handleAcknowledgment}
            />

            <div className="mx-auto flex h-full w-full max-w-7xl flex-col">
              <ShopInsuranceHeader
                isLoading={isLoading}
                error={error}
              />
              <div className="flex h-full w-full flex-col pb-32">
                {isAnyLoading && (
                  <div
                    className="my-auto"
                    data-testid="shop-insurance-modal-loading"
                  >
                    <LoadingSpinner testId="shop-insurance-modal-loading-spinner" />
                  </div>
                )}
                {anyError && (
                  <p
                    className="my-auto px-4 text-center font-medium text-red-500"
                    data-testid="shop-insurance-modal-error-message"
                  >
                    This functionality is not available in your state yet.
                  </p>
                )}

                {noLoadingNoError && (
                  <div className="h-full">
                    <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:items-start lg:gap-x-10">
                      <ShopInsuranceFiltersSidebar {...shopInsuranceFiltersProps} />

                      <div className="w-full">
                        {/* Active Filters */}
                        {(isAdjusted.length > 0 ||
                          (showEligibilityFilter && selectedEligibilityFilters.length > 0) ||
                          selectedFeatureBenefitsFilters.length > 0) && (
                          <AdjustedQuotesBanner
                            premiumValue={premiumValue}
                            benefitDurationValue={benefitDurationValue}
                            inflationProtectionValue={inflationProtectionValue}
                            appliedFilters={isAdjusted}
                            selectedEligibilityFilters={
                              showEligibilityFilter ? selectedEligibilityFilters : []
                            }
                            onRemoveEligibilityFilter={filter => {
                              const newFilters = selectedEligibilityFilters.filter(
                                item => item !== filter,
                              );
                              setSelectedEligibilityFilters(newFilters);
                            }}
                            selectedFeatureBenefitsFilters={selectedFeatureBenefitsFilters}
                            onRemoveFeatureBenefitsFilter={filter => {
                              const newFilters = selectedFeatureBenefitsFilters.filter(
                                item => item !== filter,
                              );
                              setSelectedFeatureBenefitsFilters(newFilters);
                            }}
                          />
                        )}

                        <ShopInsuranceTabsWrapper
                          hasPartnerWithDemographics={hasPartnerWithDemographics}
                          isMarriedOrPartnered={isMarriedOrPartnered}
                          paymentOption={paymentOption}
                          selectedTab={selectedTab}
                          selectedBuyerOption={selectedBuyerOption}
                          tabContent={tabContent}
                          handleTabChangeWithFetch={handleTabChangeWithFetch}
                          handleBuyerOptionChange={handleBuyerOptionChange}
                          featureFlags={featureFlags}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="fixed bottom-0 left-0 right-0 z-[100] hidden lg:block">
              <ShopInsuranceNoticeBannerDesktop />
            </div>
          </FormProvider>
        </ShopInsuranceContext.Provider>

        {/* Underwriting Details Slideout */}
        <UnderwritingSlideOut
          open={showUnderwritingSlideOut}
          onClose={() => setShowUnderwritingSlideOut(false)}
          underwritingData={selectedUnderwritingData?.data || null}
          productName={selectedUnderwritingData?.productName || ''}
        />
      </DialogContent>

      {/* Mobile only - fixed disclaimer banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] lg:hidden">
        <ShopInsuranceNoticeBannerMobile />
      </div>
    </Dialog>
  );
}
